/**
 * Voice Call System for CoandaView Collaboration
 * WebRTC-based peer-to-peer audio calls
 * Server only handles signaling, media flows directly between peers
 */

class VoiceCallManager {
    constructor() {
        this.ws = null;
        this.sessionId = null;
        this.userId = null;
        this.userName = null;

        // WebRTC state
        this.localStream = null;
        this.peerConnections = new Map();  // peerId -> RTCPeerConnection
        this.remoteStreams = new Map();    // peerId -> MediaStream
        this.pendingIceCandidates = new Map(); // peerId -> candidates[]

        // Call state
        this.isInCall = false;
        this.isMuted = false;
        this.isPanelMinimized = false;
        this.callParticipants = new Set();
        this.participantMuteStatus = new Map();
        this.connectionStates = new Map();

        // Audio elements
        this.audioElements = new Map();

        // WebRTC configuration - STUN + TURN servers for NAT traversal
        // TURN server is required for users behind symmetric NAT (common in corporate networks)
        this.rtcConfiguration = {
            iceServers: [
                { urls: 'stun:stun.l.google.com:19302' },
                { urls: 'stun:stun1.l.google.com:19302' },
                // Self-hosted TURN server for CoandaCloud
                {
                    urls: 'turn:view.coandacloud.ch:3478',
                    username: 'coandaview',
                    credential: 'CoandaTurn2024Secure!'
                },
                {
                    urls: 'turn:view.coandacloud.ch:3478?transport=tcp',
                    username: 'coandaview',
                    credential: 'CoandaTurn2024Secure!'
                }
            ],
            iceCandidatePoolSize: 10,
            bundlePolicy: 'max-bundle',
            rtcpMuxPolicy: 'require',
            iceTransportPolicy: 'all'
        };

        // Reconnect tracking to prevent infinite loops
        this.reconnectAttempts = new Map();  // peerId -> attempt count
        this.maxReconnectAttempts = 3;

        // Audio context for volume monitoring
        this.audioContext = null;
        this.volumeAnalyzers = new Map();

        // Sound effects
        this.initializeAudioFeedback();
    }

    initialize(websocket, sessionId, userId, userName) {
        this.ws = websocket;
        this.sessionId = sessionId;
        this.userId = userId;
        this.userName = userName;

        this.createCallUI();
        console.log('✅ VoiceCallManager initialized');
    }

    // ===== Call Control =====

    async startCall() {
        if (this.isInCall) {
            console.log('[VoiceCall] Already in call');
            return;
        }

        try {
            // Clear any incoming call notification
            if (this._incomingCallTimeout) {
                clearTimeout(this._incomingCallTimeout);
                this._incomingCallTimeout = null;
            }
            const callBtn = document.getElementById('voice-call-btn');
            if (callBtn) callBtn.classList.remove('incoming-call');
            const badge = document.getElementById('voice-call-badge');
            if (badge) badge.classList.remove('incoming');

            // Request microphone access FIRST
            console.log('[VoiceCall] Requesting microphone access...');
            await this.requestMicrophoneAccess();
            console.log('[VoiceCall] Microphone access granted, tracks:', this.localStream?.getTracks().length);

            // Set state BEFORE signaling so we're ready to handle incoming offers
            this.isInCall = true;

            // Join voice room (signals to other users)
            this.sendSignal('voice_join', {});

            this.updateUI();
            this.playSound('joined');

            // Start local volume monitoring so user can see their mic activity
            this.startLocalVolumeMonitoring();

            console.log('[VoiceCall] Started call, ready for peer connections');
        } catch (error) {
            console.error('[VoiceCall] Failed to start call:', error);
            Utils?.notify?.('Failed to access microphone: ' + error.message, 'error');
        }
    }

    async endCall() {
        if (!this.isInCall) return;

        // Signal leaving
        this.sendSignal('voice_leave', {});

        // Close all peer connections
        this.peerConnections.forEach((pc, peerId) => {
            pc.close();
            this.cleanupPeer(peerId);
        });
        this.peerConnections.clear();

        // Stop local volume monitoring
        this.stopLocalVolumeMonitoring();

        // Stop local stream
        if (this.localStream) {
            this.localStream.getTracks().forEach(track => track.stop());
            this.localStream = null;
        }

        // Clear state
        this.callParticipants.clear();
        this.remoteStreams.clear();
        this.isInCall = false;
        this.isMuted = false;

        this.updateUI();
        this.playSound('left');

        console.log('[VoiceCall] Ended call');
    }

    toggleMute() {
        if (!this.localStream) return;

        this.isMuted = !this.isMuted;
        this.localStream.getAudioTracks().forEach(track => {
            track.enabled = !this.isMuted;
        });

        // Broadcast mute status to peers
        this.sendSignal('voice_mute', { muted: this.isMuted });
        this.updateUI();
    }

    // ===== Microphone Access =====

    async requestMicrophoneAccess() {
        this.localStream = await navigator.mediaDevices.getUserMedia({
            audio: {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
                sampleRate: 48000
            },
            video: false
        });
    }

    // ===== Signaling =====

    sendSignal(type, data) {
        if (this.ws?.readyState !== WebSocket.OPEN) return;

        this.ws.send(JSON.stringify({
            type: type,
            ...data
        }));
    }

    handleSignalingMessage(message) {
        const { type, user_id, user_name } = message;

        // Ignore our own messages
        if (user_id === this.userId) return;

        switch (type) {
            case 'voice_user_joined':
                this.onUserJoinedCall(user_id, user_name);
                // Notify other users if they're not in the call
                if (!this.isInCall) {
                    this.notifyIncomingCall(user_name);
                }
                break;

            case 'voice_user_left':
                this.onUserLeftCall(user_id);
                break;

            case 'voice_participants':
                this.onParticipantsList(message.participants || []);
                break;

            case 'voice_offer':
                this.handleOffer(user_id, message.offer);
                break;

            case 'voice_answer':
                this.handleAnswer(user_id, message.answer);
                break;

            case 'voice_ice_candidate':
                this.handleIceCandidate(user_id, message.candidate);
                break;

            case 'voice_mute':
                this.onPeerMuteStatusChanged(user_id, message.muted);
                break;
        }
    }

    // ===== WebRTC Peer Connection =====

    async createPeerConnection(peerId) {
        if (this.peerConnections.has(peerId)) {
            return this.peerConnections.get(peerId);
        }

        const pc = new RTCPeerConnection(this.rtcConfiguration);

        // Add local audio track
        if (this.localStream) {
            this.localStream.getTracks().forEach(track => {
                pc.addTrack(track, this.localStream);
            });
        }

        // Handle incoming audio
        pc.ontrack = (event) => {
            console.log('[VoiceCall] Received remote track from', peerId);
            const remoteStream = event.streams[0];
            this.remoteStreams.set(peerId, remoteStream);
            this.playRemoteAudio(peerId, remoteStream);
        };

        // Handle ICE candidates
        pc.onicecandidate = (event) => {
            if (event.candidate) {
                this.sendSignal('voice_ice_candidate', {
                    target_user_id: peerId,
                    candidate: event.candidate.toJSON()
                });
            }
        };

        // Handle connection state
        pc.onconnectionstatechange = () => {
            const state = pc.connectionState;
            console.log(`[VoiceCall] Connection to ${peerId}: ${state}`);
            this.connectionStates.set(peerId, state);

            if (state === 'connected') {
                this.playSound('connected');
                this.onConnectionSuccess(peerId);  // Reset reconnect counter
            } else if (state === 'failed') {
                // Only reconnect on 'failed', not 'disconnected'
                // 'disconnected' can be temporary (network hiccup)
                this.handleConnectionFailure(peerId);
            }

            this.updateUI();
        };

        pc.oniceconnectionstatechange = () => {
            const iceState = pc.iceConnectionState;
            console.log(`[VoiceCall] ICE state for ${peerId}: ${iceState}`);

            // Handle ICE failures separately - more granular than connection state
            if (iceState === 'failed') {
                console.log(`[VoiceCall] ICE failed for ${peerId}, attempting restart...`);
                // Try ICE restart
                pc.restartIce();
            }
        };

        this.peerConnections.set(peerId, pc);
        return pc;
    }

    async onUserJoinedCall(peerId, userName) {
        console.log(`[VoiceCall] ${userName} joined the call`);
        this.callParticipants.add(peerId);

        if (!this.isInCall) return;

        // Create offer as the existing user
        const pc = await this.createPeerConnection(peerId);
        const offer = await pc.createOffer({
            offerToReceiveAudio: true,
            offerToReceiveVideo: false
        });
        await pc.setLocalDescription(offer);

        this.sendSignal('voice_offer', {
            target_user_id: peerId,
            offer: pc.localDescription.toJSON()
        });

        this.playSound('userJoined');
        this.updateUI();
    }

    onUserLeftCall(peerId) {
        console.log(`[VoiceCall] User ${peerId} left the call`);
        this.callParticipants.delete(peerId);
        this.cleanupPeer(peerId);
        this.playSound('userLeft');
        this.updateUI();
    }

    async onParticipantsList(participants) {
        // Received when we join - list of existing participants
        // The existing participants will send us offers (via onUserJoinedCall)
        // We just need to track them and be ready to answer
        for (const participant of participants) {
            if (participant.user_id !== this.userId) {
                this.callParticipants.add(participant.user_id);
                console.log(`[VoiceCall] Existing participant in call: ${participant.user_name}`);
            }
        }
        this.updateUI();
    }

    async handleOffer(peerId, offer) {
        console.log(`[VoiceCall] Received offer from ${peerId}`);

        // Wait for local stream if we're joining (race condition protection)
        // This ensures our audio tracks are added to the peer connection
        if (!this.localStream && this.isInCall) {
            console.log(`[VoiceCall] Waiting for local stream before handling offer...`);
            // Wait up to 3 seconds for microphone access
            for (let i = 0; i < 30 && !this.localStream; i++) {
                await new Promise(r => setTimeout(r, 100));
            }
        }

        const pc = await this.createPeerConnection(peerId);

        // Double-check: if we now have a local stream but it wasn't added to this PC, add it
        if (this.localStream) {
            const senders = pc.getSenders();
            const hasAudioSender = senders.some(s => s.track && s.track.kind === 'audio');
            if (!hasAudioSender) {
                console.log(`[VoiceCall] Adding local audio track to existing peer connection`);
                this.localStream.getTracks().forEach(track => {
                    pc.addTrack(track, this.localStream);
                });
            }
        }

        await pc.setRemoteDescription(new RTCSessionDescription(offer));

        // Process any pending ICE candidates
        await this.processPendingIceCandidates(peerId);

        const answer = await pc.createAnswer({
            offerToReceiveAudio: true,
            offerToReceiveVideo: false
        });
        await pc.setLocalDescription(answer);

        this.sendSignal('voice_answer', {
            target_user_id: peerId,
            answer: pc.localDescription.toJSON()
        });
    }

    async handleAnswer(peerId, answer) {
        console.log(`[VoiceCall] Received answer from ${peerId}`);

        const pc = this.peerConnections.get(peerId);
        if (!pc) return;

        if (pc.signalingState === 'have-local-offer') {
            await pc.setRemoteDescription(new RTCSessionDescription(answer));
            await this.processPendingIceCandidates(peerId);
        }
    }

    async handleIceCandidate(peerId, candidate) {
        const pc = this.peerConnections.get(peerId);

        if (pc && pc.remoteDescription) {
            await pc.addIceCandidate(new RTCIceCandidate(candidate));
        } else {
            // Queue candidate until remote description is set
            if (!this.pendingIceCandidates.has(peerId)) {
                this.pendingIceCandidates.set(peerId, []);
            }
            this.pendingIceCandidates.get(peerId).push(candidate);
        }
    }

    async processPendingIceCandidates(peerId) {
        const pending = this.pendingIceCandidates.get(peerId);
        if (!pending || pending.length === 0) return;

        const pc = this.peerConnections.get(peerId);
        if (!pc) return;

        for (const candidate of pending) {
            try {
                await pc.addIceCandidate(new RTCIceCandidate(candidate));
            } catch (e) {
                console.warn('[VoiceCall] Failed to add ICE candidate:', e);
            }
        }
        this.pendingIceCandidates.delete(peerId);
    }

    handleConnectionFailure(peerId) {
        // Track reconnect attempts to prevent infinite loops
        const attempts = (this.reconnectAttempts.get(peerId) || 0) + 1;
        this.reconnectAttempts.set(peerId, attempts);

        if (attempts > this.maxReconnectAttempts) {
            console.warn(`[VoiceCall] Max reconnect attempts (${this.maxReconnectAttempts}) reached for ${peerId}, giving up`);
            Utils?.notify?.('Voice connection unstable. The other user may be behind a restrictive firewall.', 'warning');
            return;
        }

        console.log(`[VoiceCall] Connection failed to ${peerId}, attempting reconnect (${attempts}/${this.maxReconnectAttempts})...`);

        // Try to reconnect after an increasing delay
        const delay = 2000 * attempts;  // 2s, 4s, 6s
        setTimeout(async () => {
            if (this.isInCall && this.callParticipants.has(peerId)) {
                this.cleanupPeer(peerId);
                await this.createPeerConnection(peerId);

                // Re-send offer
                const pc = this.peerConnections.get(peerId);
                if (pc) {
                    const offer = await pc.createOffer({
                        offerToReceiveAudio: true,
                        offerToReceiveVideo: false,
                        iceRestart: true  // Request ICE restart
                    });
                    await pc.setLocalDescription(offer);
                    this.sendSignal('voice_offer', {
                        target_user_id: peerId,
                        offer: pc.localDescription.toJSON()
                    });
                }
            }
        }, delay);
    }

    // Reset reconnect attempts when connection succeeds
    onConnectionSuccess(peerId) {
        this.reconnectAttempts.delete(peerId);
    }

    cleanupPeer(peerId) {
        // Close connection
        const pc = this.peerConnections.get(peerId);
        if (pc) {
            pc.close();
            this.peerConnections.delete(peerId);
        }

        // Remove audio element
        const audioEl = this.audioElements.get(peerId);
        if (audioEl) {
            audioEl.srcObject = null;
            audioEl.remove();
            this.audioElements.delete(peerId);
        }

        // Clear streams
        this.remoteStreams.delete(peerId);
        this.connectionStates.delete(peerId);
        this.pendingIceCandidates.delete(peerId);
        this.participantMuteStatus.delete(peerId);

        // Stop volume analyzer
        const analyzer = this.volumeAnalyzers.get(peerId);
        if (analyzer) {
            clearInterval(analyzer.intervalId);
            this.volumeAnalyzers.delete(peerId);
        }
    }

    onPeerMuteStatusChanged(peerId, muted) {
        this.participantMuteStatus.set(peerId, muted);
        this.updateUI();
    }

    // ===== Audio Playback =====

    playRemoteAudio(peerId, stream) {
        // Remove existing audio element
        const existing = this.audioElements.get(peerId);
        if (existing) {
            existing.remove();
        }

        const audio = document.createElement('audio');
        audio.id = `voice-audio-${peerId}`;
        audio.srcObject = stream;
        audio.autoplay = true;
        audio.style.display = 'none';
        document.body.appendChild(audio);

        this.audioElements.set(peerId, audio);

        // Handle autoplay restrictions
        audio.play().catch(err => {
            console.warn('[VoiceCall] Autoplay blocked, will play on user interaction');
        });

        // Start volume monitoring
        this.startVolumeMonitoring(peerId, stream);
    }

    startVolumeMonitoring(peerId, stream) {
        if (!this.audioContext) {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        }

        const analyser = this.audioContext.createAnalyser();
        const source = this.audioContext.createMediaStreamSource(stream);
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.8;
        source.connect(analyser);

        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        const intervalId = setInterval(() => {
            analyser.getByteFrequencyData(dataArray);
            let sum = 0;
            for (let i = 0; i < dataArray.length; i++) {
                sum += dataArray[i] * dataArray[i];
            }
            const rms = Math.sqrt(sum / dataArray.length);
            const volume = Math.min(100, Math.round((rms / 128) * 100));

            // Update UI volume indicator
            const indicator = document.querySelector(`#voice-participant-${peerId} .volume-bar`);
            if (indicator) {
                indicator.style.width = `${volume}%`;
            }
        }, 100);

        this.volumeAnalyzers.set(peerId, { analyser, source, intervalId });
    }

    startLocalVolumeMonitoring() {
        if (!this.localStream) return;

        if (!this.audioContext) {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        }

        const analyser = this.audioContext.createAnalyser();
        const source = this.audioContext.createMediaStreamSource(this.localStream);
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.8;
        source.connect(analyser);
        // Note: Don't connect analyser to destination - we only want to monitor, not play back local audio

        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        const intervalId = setInterval(() => {
            if (!this.isInCall) {
                clearInterval(intervalId);
                return;
            }

            analyser.getByteFrequencyData(dataArray);
            let sum = 0;
            for (let i = 0; i < dataArray.length; i++) {
                sum += dataArray[i] * dataArray[i];
            }
            const rms = Math.sqrt(sum / dataArray.length);
            const volume = Math.min(100, Math.round((rms / 128) * 100));

            // Update UI volume indicator for self
            const indicator = document.querySelector(`#voice-participant-${this.userId} .volume-bar`);
            if (indicator) {
                indicator.style.width = `${volume}%`;
            }
        }, 100);

        // Store with a special key for the local stream
        this.volumeAnalyzers.set('local', { analyser, source, intervalId });
    }

    stopLocalVolumeMonitoring() {
        const localAnalyzer = this.volumeAnalyzers.get('local');
        if (localAnalyzer) {
            clearInterval(localAnalyzer.intervalId);
            this.volumeAnalyzers.delete('local');
        }
    }

    // ===== Audio Feedback =====

    initializeAudioFeedback() {
        this.sounds = {};
    }

    playSound(soundName) {
        try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = ctx.createOscillator();
            const gain = ctx.createGain();

            oscillator.connect(gain);
            gain.connect(ctx.destination);

            switch (soundName) {
                case 'joined':
                case 'connected':
                    oscillator.frequency.value = 880;
                    gain.gain.setValueAtTime(0.1, ctx.currentTime);
                    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
                    oscillator.start(ctx.currentTime);
                    oscillator.stop(ctx.currentTime + 0.2);
                    break;

                case 'left':
                case 'userLeft':
                    oscillator.frequency.value = 440;
                    gain.gain.setValueAtTime(0.1, ctx.currentTime);
                    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
                    oscillator.start(ctx.currentTime);
                    oscillator.stop(ctx.currentTime + 0.3);
                    break;

                case 'userJoined':
                    oscillator.frequency.value = 660;
                    gain.gain.setValueAtTime(0.08, ctx.currentTime);
                    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
                    oscillator.start(ctx.currentTime);
                    oscillator.stop(ctx.currentTime + 0.15);
                    break;
            }
        } catch (e) {
            // Audio context not available
        }
    }

    // ===== UI =====

    createCallUI(retryCount = 0) {
        // Check if button already exists
        if (document.getElementById('voice-call-btn')) return;

        console.log('[VoiceCall] Creating floating call button');

        // Create floating call button (next to chat button)
        const callBtn = document.createElement('button');
        callBtn.id = 'voice-call-btn';
        callBtn.className = 'voice-call-button';
        callBtn.innerHTML = `
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
            </svg>
            <span class="voice-call-badge" id="voice-call-badge" style="display: none;">0</span>
        `;
        callBtn.title = 'Join Voice Call';
        callBtn.addEventListener('click', () => this.toggleCall());
        document.body.appendChild(callBtn);

        // Create participants panel
        this.createParticipantsPanel();

        // Inject styles
        this.injectStyles();
    }

    createParticipantsPanel() {
        if (document.getElementById('voice-participants-panel')) return;

        const panel = document.createElement('div');
        panel.id = 'voice-participants-panel';
        panel.className = 'voice-participants-panel';
        panel.style.display = 'none';
        panel.innerHTML = `
            <div class="voice-panel-header">
                <span>Voice Call</span>
                <div class="voice-panel-controls">
                    <button id="voice-minimize-btn" class="voice-control-btn" title="Minimize">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="4 14 10 14 10 20"></polyline>
                            <polyline points="20 10 14 10 14 4"></polyline>
                            <line x1="14" y1="10" x2="21" y2="3"></line>
                            <line x1="3" y1="21" x2="10" y2="14"></line>
                        </svg>
                    </button>
                    <button id="voice-mute-btn" class="voice-control-btn" title="Toggle Mute">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                            <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                            <line x1="12" y1="19" x2="12" y2="23"></line>
                            <line x1="8" y1="23" x2="16" y2="23"></line>
                        </svg>
                    </button>
                    <button id="voice-end-btn" class="voice-control-btn voice-end" title="End Call">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91"></path>
                            <line x1="1" y1="1" x2="23" y2="23"></line>
                        </svg>
                    </button>
                </div>
            </div>
            <div id="voice-participants-list" class="voice-participants-list">
                <!-- Participants will be added here -->
            </div>
        `;
        document.body.appendChild(panel);

        // Event listeners
        document.getElementById('voice-minimize-btn').addEventListener('click', () => this.togglePanelMinimize());
        document.getElementById('voice-mute-btn').addEventListener('click', () => this.toggleMute());
        document.getElementById('voice-end-btn').addEventListener('click', () => this.endCall());
    }

    togglePanelMinimize() {
        this.isPanelMinimized = !this.isPanelMinimized;
        this.updateUI();
    }

    toggleCall() {
        if (this.isInCall) {
            this.endCall();
        } else {
            this.startCall();
        }
    }

    updateUI() {
        const callBtn = document.getElementById('voice-call-btn');
        const panel = document.getElementById('voice-participants-panel');
        const muteBtn = document.getElementById('voice-mute-btn');
        const minimizeBtn = document.getElementById('voice-minimize-btn');
        const participantsList = document.getElementById('voice-participants-list');
        const badge = document.getElementById('voice-call-badge');

        if (!callBtn) return;

        if (this.isInCall) {
            callBtn.classList.add('in-call');
            callBtn.title = 'Leave Call';
            // Change icon to phone-off when in call
            callBtn.innerHTML = `
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
                <span class="voice-call-badge" id="voice-call-badge" style="display: none;">0</span>
            `;

            // Handle minimized/expanded panel state
            if (this.isPanelMinimized) {
                panel.classList.add('minimized');
                panel.style.display = 'block';
                // Update minimize button icon to expand
                if (minimizeBtn) {
                    minimizeBtn.title = 'Expand';
                    minimizeBtn.innerHTML = `
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="15 3 21 3 21 9"></polyline>
                            <polyline points="9 21 3 21 3 15"></polyline>
                            <line x1="21" y1="3" x2="14" y2="10"></line>
                            <line x1="3" y1="21" x2="10" y2="14"></line>
                        </svg>
                    `;
                }
            } else {
                panel.classList.remove('minimized');
                panel.style.display = 'block';
                // Update minimize button icon to minimize
                if (minimizeBtn) {
                    minimizeBtn.title = 'Minimize';
                    minimizeBtn.innerHTML = `
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="4 14 10 14 10 20"></polyline>
                            <polyline points="20 10 14 10 14 4"></polyline>
                            <line x1="14" y1="10" x2="21" y2="3"></line>
                            <line x1="3" y1="21" x2="10" y2="14"></line>
                        </svg>
                    `;
                }
            }

            // Show participant count badge
            const participantCount = this.callParticipants.size;
            if (participantCount > 0 && badge) {
                badge.textContent = participantCount;
                badge.style.display = 'flex';
            }

            // Update mute button
            if (this.isMuted) {
                muteBtn.classList.add('muted');
                muteBtn.innerHTML = `
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="1" y1="1" x2="23" y2="23"></line>
                        <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path>
                        <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"></path>
                        <line x1="12" y1="19" x2="12" y2="23"></line>
                        <line x1="8" y1="23" x2="16" y2="23"></line>
                    </svg>
                `;
            } else {
                muteBtn.classList.remove('muted');
                muteBtn.innerHTML = `
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                        <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                        <line x1="12" y1="19" x2="12" y2="23"></line>
                        <line x1="8" y1="23" x2="16" y2="23"></line>
                    </svg>
                `;
            }

            // Update participants list
            this.updateParticipantsList(participantsList);
        } else {
            callBtn.classList.remove('in-call');
            callBtn.classList.remove('incoming-call');
            callBtn.title = 'Join Voice Call';
            // Restore phone icon when not in call
            callBtn.innerHTML = `
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                </svg>
                <span class="voice-call-badge" id="voice-call-badge" style="display: none;">0</span>
            `;
            panel.style.display = 'none';
            this.isPanelMinimized = false;  // Reset minimized state when call ends
        }
    }

    // Notify user about an incoming call with blinking button and sound
    notifyIncomingCall(userName) {
        const callBtn = document.getElementById('voice-call-btn');
        if (!callBtn || this.isInCall) return;

        // Add blinking animation
        callBtn.classList.add('incoming-call');

        // Play incoming call sound (ring tone)
        this.playIncomingCallSound();

        // Show notification
        Utils?.notify?.(`${userName} started a voice call. Click the phone button to join!`, 'info');

        // Update badge to show active call
        const badge = document.getElementById('voice-call-badge');
        if (badge) {
            badge.textContent = '!';
            badge.style.display = 'flex';
            badge.classList.add('incoming');
        }

        // Stop blinking after 15 seconds
        this._incomingCallTimeout = setTimeout(() => {
            callBtn.classList.remove('incoming-call');
            if (badge) badge.classList.remove('incoming');
        }, 15000);
    }

    playIncomingCallSound() {
        try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();

            // Play a ringtone-like sound (two alternating tones)
            const playTone = (freq, startTime, duration) => {
                const oscillator = ctx.createOscillator();
                const gain = ctx.createGain();

                oscillator.connect(gain);
                gain.connect(ctx.destination);

                oscillator.frequency.value = freq;
                oscillator.type = 'sine';

                gain.gain.setValueAtTime(0.15, startTime);
                gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

                oscillator.start(startTime);
                oscillator.stop(startTime + duration);
            };

            // Ring pattern: two tones, pause, repeat
            const now = ctx.currentTime;
            for (let i = 0; i < 3; i++) {
                const offset = i * 0.8;
                playTone(523.25, now + offset, 0.15);       // C5
                playTone(659.25, now + offset + 0.15, 0.15); // E5
                playTone(523.25, now + offset + 0.3, 0.15);  // C5
            }
        } catch (e) {
            console.warn('[VoiceCall] Could not play incoming call sound:', e);
        }
    }

    updateParticipantsList(container) {
        if (!container) return;
        container.innerHTML = '';

        // Add self
        const selfEl = this.createParticipantElement(this.userId, this.userName, this.isMuted, true);
        container.appendChild(selfEl);

        // Add others
        this.callParticipants.forEach(peerId => {
            const peerName = this.getPeerName(peerId);
            const muted = this.participantMuteStatus.get(peerId) || false;
            const state = this.connectionStates.get(peerId) || 'connecting';
            const el = this.createParticipantElement(peerId, peerName, muted, false, state);
            container.appendChild(el);
        });
    }

    createParticipantElement(peerId, name, muted, isSelf, connectionState = 'connected') {
        const el = document.createElement('div');
        el.id = `voice-participant-${peerId}`;
        el.className = `voice-participant ${isSelf ? 'self' : ''} ${muted ? 'muted' : ''}`;

        const statusClass = connectionState === 'connected' ? 'connected' :
                           connectionState === 'connecting' ? 'connecting' : 'disconnected';

        el.innerHTML = `
            <div class="participant-avatar" style="background: ${this.getUserColor(peerId)}">
                ${name.substring(0, 2).toUpperCase()}
            </div>
            <div class="participant-info">
                <span class="participant-name">${name}${isSelf ? ' (You)' : ''}</span>
                <div class="participant-status">
                    <span class="status-dot ${statusClass}"></span>
                    <span class="status-text">${connectionState}</span>
                </div>
            </div>
            <div class="participant-indicators">
                ${muted ? '<span class="mute-indicator">🔇</span>' : ''}
                <div class="volume-bar-container">
                    <div class="volume-bar"></div>
                </div>
            </div>
        `;

        return el;
    }

    getPeerName(peerId) {
        // Try to get name from presence manager
        if (window.presenceManager?.activeUsers?.has(peerId)) {
            return window.presenceManager.activeUsers.get(peerId).name;
        }
        return 'Unknown';
    }

    getUserColor(userId) {
        const colors = [
            '#FF5722', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5',
            '#2196F3', '#03A9F4', '#00BCD4', '#009688', '#4CAF50'
        ];
        let hash = 0;
        for (let i = 0; i < userId.length; i++) {
            hash = userId.charCodeAt(i) + ((hash << 5) - hash);
        }
        return colors[Math.abs(hash) % colors.length];
    }

    getCallStatus() {
        return {
            isInCall: this.isInCall,
            isMuted: this.isMuted,
            participants: Array.from(this.callParticipants),
            participantMuteStatus: Object.fromEntries(this.participantMuteStatus)
        };
    }

    destroy() {
        this.endCall();
        document.getElementById('voice-call-btn')?.remove();
        document.getElementById('voice-participants-panel')?.remove();
    }

    injectStyles() {
        if (document.getElementById('voice-call-styles')) return;

        const style = document.createElement('style');
        style.id = 'voice-call-styles';
        style.textContent = `
            /* Floating Voice Call Button (above chat button) */
            .voice-call-button {
                position: fixed;
                bottom: 90px;
                right: 24px;
                width: 56px;
                height: 56px;
                border-radius: 50%;
                background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
                border: none;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                color: white;
                box-shadow: 0 4px 20px rgba(76, 175, 80, 0.4);
                transition: all 0.3s ease;
                z-index: 9999;
            }

            .voice-call-button:hover {
                transform: scale(1.1);
                box-shadow: 0 6px 30px rgba(76, 175, 80, 0.6);
            }

            .voice-call-button.in-call {
                background: linear-gradient(135deg, #f44336 0%, #d32f2f 100%);
                box-shadow: 0 4px 20px rgba(244, 67, 54, 0.4);
                animation: pulse-call 2s infinite;
            }

            .voice-call-badge {
                position: absolute;
                top: -4px;
                right: -4px;
                background: #2196F3;
                color: white;
                font-size: 11px;
                font-weight: bold;
                min-width: 20px;
                height: 20px;
                border-radius: 10px;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0 6px;
            }

            @keyframes pulse-call {
                0%, 100% { box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.4); }
                50% { box-shadow: 0 0 0 10px rgba(244, 67, 54, 0); }
            }

            .voice-participants-panel {
                position: fixed;
                bottom: 160px;
                right: 24px;
                width: 280px;
                background: #1e1e2e;
                border-radius: 12px;
                box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
                z-index: 1001;
                overflow: hidden;
            }

            .voice-panel-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 12px 16px;
                background: linear-gradient(135deg, #4CAF50, #45a049);
                color: white;
                font-weight: 600;
                font-size: 14px;
            }

            .voice-panel-controls {
                display: flex;
                gap: 8px;
            }

            .voice-control-btn {
                width: 32px;
                height: 32px;
                border-radius: 50%;
                background: rgba(255, 255, 255, 0.2);
                border: none;
                color: white;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                transition: all 0.2s;
            }

            .voice-control-btn:hover {
                background: rgba(255, 255, 255, 0.3);
            }

            .voice-control-btn.muted {
                background: rgba(255, 87, 34, 0.8);
            }

            .voice-control-btn.voice-end {
                background: rgba(244, 67, 54, 0.8);
            }

            .voice-control-btn.voice-end:hover {
                background: rgba(244, 67, 54, 1);
            }

            .voice-participants-list {
                padding: 8px;
                max-height: 300px;
                overflow-y: auto;
            }

            .voice-participant {
                display: flex;
                align-items: center;
                gap: 10px;
                padding: 8px 10px;
                border-radius: 8px;
                background: rgba(255, 255, 255, 0.05);
                margin-bottom: 6px;
            }

            .voice-participant.self {
                background: rgba(76, 175, 80, 0.1);
                border: 1px solid rgba(76, 175, 80, 0.3);
            }

            .voice-participant.muted {
                opacity: 0.7;
            }

            .participant-avatar {
                width: 36px;
                height: 36px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 12px;
                font-weight: 600;
                color: white;
            }

            .participant-info {
                flex: 1;
            }

            .participant-name {
                display: block;
                color: white;
                font-size: 13px;
                font-weight: 500;
            }

            .participant-status {
                display: flex;
                align-items: center;
                gap: 4px;
                margin-top: 2px;
            }

            .status-dot {
                width: 6px;
                height: 6px;
                border-radius: 50%;
            }

            .status-dot.connected { background: #4CAF50; }
            .status-dot.connecting { background: #FFC107; animation: blink 1s infinite; }
            .status-dot.disconnected { background: #F44336; }

            @keyframes blink {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.3; }
            }

            .status-text {
                font-size: 10px;
                color: rgba(255, 255, 255, 0.5);
                text-transform: capitalize;
            }

            .participant-indicators {
                display: flex;
                align-items: center;
                gap: 6px;
            }

            .mute-indicator {
                font-size: 14px;
            }

            .volume-bar-container {
                width: 40px;
                height: 4px;
                background: rgba(255, 255, 255, 0.1);
                border-radius: 2px;
                overflow: hidden;
            }

            .volume-bar {
                height: 100%;
                background: #4CAF50;
                width: 0%;
                transition: width 0.1s;
            }

            /* Minimized panel state */
            .voice-participants-panel.minimized {
                width: auto;
                min-width: 180px;
            }

            .voice-participants-panel.minimized .voice-participants-list {
                display: none;
            }

            .voice-participants-panel.minimized .voice-panel-header {
                padding: 10px 14px;
                border-radius: 12px;
            }

            .voice-participants-panel.minimized .voice-panel-header span:first-child {
                font-size: 12px;
            }

            /* Incoming call notification animation */
            .voice-call-button.incoming-call {
                animation: incoming-ring 0.5s ease-in-out infinite;
                background: linear-gradient(135deg, #FF9800 0%, #F57C00 100%);
                box-shadow: 0 4px 20px rgba(255, 152, 0, 0.6);
            }

            @keyframes incoming-ring {
                0%, 100% {
                    transform: scale(1) rotate(0deg);
                    box-shadow: 0 0 0 0 rgba(255, 152, 0, 0.7);
                }
                25% {
                    transform: scale(1.05) rotate(-5deg);
                }
                50% {
                    transform: scale(1.1) rotate(5deg);
                    box-shadow: 0 0 0 15px rgba(255, 152, 0, 0);
                }
                75% {
                    transform: scale(1.05) rotate(-3deg);
                }
            }

            .voice-call-badge.incoming {
                background: #FF5722;
                animation: badge-pulse 0.8s ease-in-out infinite;
            }

            @keyframes badge-pulse {
                0%, 100% { transform: scale(1); }
                50% { transform: scale(1.2); }
            }
        `;
        document.head.appendChild(style);
    }
}

// Create global instance
window.voiceCallManager = new VoiceCallManager();
